<template>
  <div class="flex items-center space-x-1">
    <button
      type="button"
      class="flex items-center justify-center"
      @click="decrementValue"
    >
      <IconChevronLeft />
    </button>
    <button
      type="button"
      class="flex min-w-[10.5rem] items-center justify-between rounded-full bg-background p-1 text-center"
      @click="showDrawer"
    >
      <span
        class="mx-2 line-clamp-1 flex-1 text-center text-xs font-medium"
      >
        {{ label }}
      </span>
      <IconChevronDown
        class="rounded-full bg-background-fg/10"
      />
    </button>
    <button
      type="button"
      class="flex items-center justify-center disabled:opacity-20"
      :disabled="isIncrementDisabled"
      @click="incrementValue(1)"
    >
      <IconChevronRight />
    </button>
  </div>
  <BaseDialog :is-open="isOpen" @close="onClose">
    <div class="px-2 py-4">
      <DateSelect
        :model-value="modelValue"
        :type="type"
        :disable-current-month="disableCurrentMonth"
        @update:model-value="onUpdate"
      />
    </div>
  </BaseDialog>
</template>

<script setup lang="ts">
import * as DateFns from "date-fns"

const props = defineProps<{
  modelValue: string
  type: "day" | "month"
  disableCurrentMonth?: boolean
}>()

const emit = defineEmits(["update:modelValue"])

const internalModel = computed(() =>
  props.modelValue
    ? DateFns.parse(
        props.modelValue,
        "yyyy-MM-dd",
        new Date()
      )
    : undefined
)
const isOpen = ref(false)
const showDrawer = () => {
  isOpen.value = true
}
const hideDrawer = () => {
  isOpen.value = false
}

const label = computed(() =>
  internalModel.value
    ? props.type === "day"
      ? formatToDate(internalModel.value)
      : formatToMonth(internalModel.value)
    : "All Time"
)

const onClose = () => {
  hideDrawer()
}

const onUpdate = (value: typeof props.modelValue) => {
  emit("update:modelValue", value)
  hideDrawer()
}

const getNextDate = (step: number) => {
  const modelValueAsDate = DateFns.parse(
    props.modelValue ?? "",
    "yyyy-MM-dd",
    new Date()
  )

  if (!DateFns.isValid(modelValueAsDate)) return null

  return props.type === "day"
    ? DateFns.addDays(modelValueAsDate, step)
    : DateFns.addMonths(modelValueAsDate, step)
}

const incrementValue = (step = 1) => {
  const nextDate = getNextDate(step)
  if (nextDate)
    emit(
      "update:modelValue",
      DateFns.format(nextDate, "yyyy-MM-dd")
    )
}

const decrementValue = () => {
  incrementValue(-1)
}

const isIncrementDisabled = computed(() => {
  const nextDate = getNextDate(1)

  if (!nextDate) return true

  if (
    props.disableCurrentMonth &&
    DateFns.isThisMonth(nextDate)
  )
    return true

  return DateFns.isAfter(nextDate, DateFns.endOfToday())
})
</script>

<style lang="scss" module>
.squareHack {
  position: relative;
  padding-top: 100%;
}
</style>
